import { useState } from "react";
import { useIntl } from "react-intl";
import { Navigate } from "react-router";
import { useMutation, useQuery } from "@apollo/client/react";
import { Switch } from "@/components/design/Switch";
import { InfoTooltip } from "@/components/design/InfoTooltip";
import {
  AccessConfigFeatureMatrixDocument,
  GrantFeatureAudienceDocument,
  RevokeFeatureAudienceDocument,
} from "@/graphql/generated/operations";
import { errorClassification, userErrorMessage } from "@/lib/user-error";
import { LocalizedError } from "@/lib/localized-error";
import { usePendingScope } from "@/lib/pending-edits";
import { FEATURE_CATALOG, type FeatureCatalogEntry } from "@/auth/feature-catalog";
import { PILLARS } from "@/components/layout/AppShell";
import { SettingsCard } from "./SettingsCard";

// Cross-references genesis's key against spark's own catalog purely to read
// the enforcement split it already tracks (feature-catalog.ts) - "server" for
// grow/search/match (a real validateFeatureReachable guard exists), "ui-only"
// for the rest (grow.map/save.community/share today): no guard exists or
// should exist, see Feature.java's class javadoc on the genesis side. An
// admin toggling a ui-only row is choosing what spark shows, not what
// genesis will refuse - the badge exists so nobody reads the two as the same
// promise.
const CATALOG_BY_KEY: Map<string, FeatureCatalogEntry> = new Map(
  Object.values(FEATURE_CATALOG).map((entry) => [entry.key, entry]),
);

// Subpage rows read like the sidebar entries they configure: the label comes
// from AppShell's PILLARS items (the same message id the menu renders), so the
// matrix and the menu never call one page two different things. Pillars stay
// on their key (SAVE/GROW/... via CSS uppercase), see renderRow.
const NAV_LABEL_BY_FEATURE_KEY: Map<string, { id: string; defaultMessage: string }> = new Map(
  PILLARS.flatMap((pillar) =>
    pillar.items.flatMap((item) =>
      item.featureKey ? [[item.featureKey, { id: item.labelId, defaultMessage: item.defaultMessage }] as const] : [],
    ),
  ),
);

// The three code-fixed system buckets and their rank, mirrored from genesis'
// own floor trigger (tfn_feature_audience_enforce_floor in V0052). These ids
// are well-known and stable by construction (0026-access-config-additive-only-by-construction
// §4.1/§4.2: a database trigger refuses every rename/delete on them), so
// hard-coding them here is safe in the way hard-coding a config-editable
// value would not be. This is a client-side PREVIEW of the server rule, not
// a second authority: genesis still refuses the write regardless, this only
// saves a round trip to a confusing generic error. A configurable
// (non-system) audience carries no rank and is never refused by the floor,
// exactly like the trigger's own `v_tag_rank is null` early return.
const ANONYMOUS = "ab315907-114b-479a-aaee-09424d15531e";
const EXTERNAL = "448c4138-fa38-4ddb-b560-db6f08a58b67";
const INTERNAL = "0a546db5-32d9-4aa6-8959-625b6f155acc";

const SYSTEM_AUDIENCE_RANK: Record<string, number> = {
  [ANONYMOUS]: 0,
  [EXTERNAL]: 1,
  [INTERNAL]: 2,
};

function isBelowFloor(audienceId: string, minAudienceId: string): boolean {
  const tagRank = SYSTEM_AUDIENCE_RANK[audienceId];
  const floorRank = SYSTEM_AUDIENCE_RANK[minAudienceId];
  if (tagRank === undefined || floorRank === undefined) return false;
  return tagRank < floorRank;
}

// Grouping falls out of the key naming convention itself (grow.map is a
// subpage of grow, save.profile.powers-source a control on save.profile) - no
// separate parent/child table needed. The parent is the LONGEST dotted prefix
// that is itself a present feature or one of the four pillars, so a control
// inside a subpage nests beneath that subpage rather than beside it. A key
// with no such prefix renders standalone.
function parentKeyOf(featureKey: string, isKnown: (key: string) => boolean): string | null {
  for (let dot = featureKey.lastIndexOf("."); dot !== -1; dot = featureKey.lastIndexOf(".", dot - 1)) {
    const prefix = featureKey.slice(0, dot);
    if (isKnown(prefix)) return prefix;
  }
  return null;
}

// genesis feature keys are all-lowercase identifiers (grow.map, save.pins);
// the matrix reads them as row labels, where that scans as unfinished. The
// four pillar rows force CSS uppercase over this regardless, so this only
// visibly matters for subpages and the Tools children.
function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// A configurable (non-system) audience has no fixed "who is this" answer, so
// it gets no InfoTooltip. Only the three code-fixed buckets have a stable
// meaning worth explaining.
function useAudienceDescriptions(): Record<string, string> {
  const intl = useIntl();
  return {
    [ANONYMOUS]: intl.formatMessage({
      id: "admin.accessConfig.audienceAnonymousBody",
      defaultMessage:
        "Any unregistered visitor. Currently theoretical: spark requires login before anyone reaches a gated route at all.",
    }),
    [EXTERNAL]: intl.formatMessage({
      id: "admin.accessConfig.audienceExternalBody",
      defaultMessage: "Registered but not internal people, e.g. external applicants.",
    }),
    [INTERNAL]: intl.formatMessage({
      id: "admin.accessConfig.audienceInternalBody",
      defaultMessage: "Logged-in employees of your organisation.",
    }),
  };
}

// Feature-whitelist admin matrix (genesis 242-access-model). Lives as a
// Settings section like Branding/Stammdaten/Fragebögen, but unlike those it
// carries no Role in ALL_SECTIONS (SettingsPage.tsx): write:access-config is
// a Genesis DB grant, not a JWT claim, so there is nothing to check
// client-side (see @/auth/access-features.tsx for the same reasoning on the
// nav side). The section entry is therefore visible to anyone who reaches
// /admin/settings at all (role: null, same bucket as Model/Styleguide); the
// real gate is here: `features` is itself guarded server-side on
// write:access-config, so a FORBIDDEN response IS "not a config admin" and
// redirects away, same reasoning as require-auth.ts.
export function AccessConfigFeaturesPanel() {
  const intl = useIntl();
  const { data, loading, error, refetch } = useQuery(AccessConfigFeatureMatrixDocument, {
    fetchPolicy: "network-only",
  });
  const [grant, { loading: granting }] = useMutation(GrantFeatureAudienceDocument);
  const [revoke, { loading: revoking }] = useMutation(RevokeFeatureAudienceDocument);
  const audienceDescriptions = useAudienceDescriptions();

  // Toggling a cell no longer fires a mutation immediately - it only edits
  // this local draft, keyed `${featureKey}::${audienceId}`, value = the
  // desired grant state. Only cells that differ from the server get an
  // entry, so `dirty` and the diff sent on save both fall out of this one
  // map for free. Persisting happens through the app-wide SaveBar
  // (usePendingScope below), same "click, then Speichern" shape as every
  // other admin form.
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const dirty = Object.keys(pending).length > 0;

  // Collapsed subpage groups, keyed by parent feature key. Collapsed by
  // default - with four pillars, Tools, and every subpage all expanded at
  // once the matrix is longer than it is readable; an admin opens a group
  // to check or change it, not to see all fourteen rows unfolded upfront.
  const [collapsedParents, setCollapsedParents] = useState<Record<string, boolean>>({});

  // usePendingScope is a hook and must run unconditionally, so it sits above
  // the FORBIDDEN early-return below even though a redirecting principal has
  // nothing to save - dirty is always false in that case anyway.
  usePendingScope("access-config-features", {
    dirty,
    save: async () => {
      try {
        await Promise.all(
          Object.entries(pending).map(([key, desiredGranted]) => {
            const [featureKey, audienceId] = key.split("::") as [string, string];
            return desiredGranted
              ? grant({ variables: { input: { featureKey, audienceId } } })
              : revoke({ variables: { input: { featureKey, audienceId } } });
          }),
        );
        setPending({});
        await refetch();
      } catch (e) {
        throw new LocalizedError(userErrorMessage(intl, e), { cause: e });
      }
    },
    discard: () => setPending({}),
  });

  if (error && errorClassification(error) === "FORBIDDEN") {
    return <Navigate to="/" replace />;
  }

  const features = [...(data?.features.features ?? [])].sort((a, b) => a.key.localeCompare(b.key));
  // Internal first, then External, then Anonymous - the three system
  // audiences ranked from most to least reach, not alphabetically (which
  // happened to read anonymous/external/internal in en and de alike, but
  // read as the wrong direction: the column an admin checks most, the
  // internal one, sat last). Any configurable (non-system, unranked)
  // audience keeps sorting alphabetically, after the three system ones.
  const audiences = [...(data?.audiences.audiences ?? [])].sort((a, b) => {
    const aRank = SYSTEM_AUDIENCE_RANK[a.id];
    const bRank = SYSTEM_AUDIENCE_RANK[b.id];
    if (aRank !== undefined && bRank !== undefined) return bRank - aRank;
    if (aRank !== undefined) return -1;
    if (bRank !== undefined) return 1;
    return (a.name?.localized ?? a.id).localeCompare(b.name?.localized ?? b.id);
  });
  const busy = granting || revoking;

  type Feature = (typeof features)[number];

  const featuresByKey = new Map(features.map((f) => [f.key, f]));

  // spark's four pillars group their own subpages under a collapsible header,
  // whether or not the pillar itself is a real genesis feature. "save" has no
  // pillar-level key (status: "planned" in feature-catalog.ts) but its four
  // subpages still need somewhere to nest - a synthetic header groups them
  // without genesis needing a "save" feature row to exist. "share"/"match"
  // have no subpages yet, so they render as plain rows with no chevron,
  // exactly as before - this list only matters once a feature under them
  // shows up.
  const PILLAR_KEYS = ["grow", "save", "share", "match"];
  // Same four keys, ordered like AppShell's own PILLARS array (the sidebar
  // heading order) rather than alphabetically - the matrix should read like
  // the menu it configures.
  const PILLAR_ORDER = ["save", "grow", "share", "match"];

  // "Tools" is a spark-only grouping, not a genesis concept: search/assistant/
  // chat are three unrelated standalone topbar-icon toggles with no shared
  // "tools." key prefix (unlike save.*/grow.*, there is no such prefix and
  // none should ever exist). Unlike "save" this group can never be promoted
  // to a real feature row - "Tools" names no page spark would ever route to -
  // so it stays synthetic permanently, purely to declutter three single-row
  // toggles into one collapsible bucket. Ordered like the topbar's own icon
  // order (TOPBAR_FEATURE_ANCHORS in feature-catalog.ts), not alphabetically.
  const TOOLS_KEY = "tools";
  const TOOLS_CHILD_KEYS = ["search", "assistant", "chat"];

  // Subpages sort like AppShell's own PILLARS[].items order (the sidebar
  // submenu order), not alphabetically - "save.profile" before
  // "save.documents" reads oddly next to a sidebar that shows Profil first.
  const PILLAR_CHILD_ORDER: Record<string, string[]> = {
    save: ["save.profile", "save.documents", "save.community", "save.pins"],
    grow: ["grow.map", "grow.watch", "grow.play"],
  };

  const isKnownParent = (key: string) => featuresByKey.has(key) || PILLAR_KEYS.includes(key);
  const childrenByParent = new Map<string, Feature[]>();
  for (const f of features) {
    const parentKey = parentKeyOf(f.key, isKnownParent);
    if (parentKey) {
      const siblings = childrenByParent.get(parentKey) ?? [];
      siblings.push(f);
      childrenByParent.set(parentKey, siblings);
    } else if (TOOLS_CHILD_KEYS.includes(f.key)) {
      const siblings = childrenByParent.get(TOOLS_KEY) ?? [];
      siblings.push(f);
      childrenByParent.set(TOOLS_KEY, siblings);
    }
  }
  childrenByParent
    .get(TOOLS_KEY)
    ?.sort((a, b) => TOOLS_CHILD_KEYS.indexOf(a.key) - TOOLS_CHILD_KEYS.indexOf(b.key));
  for (const [parentKey, order] of Object.entries(PILLAR_CHILD_ORDER)) {
    childrenByParent.get(parentKey)?.sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
  }
  // Top-level = every feature that is not itself a pillar's subpage or a
  // Tools entry.
  const topLevelFeatures = features.filter(
    (f) => parentKeyOf(f.key, isKnownParent) === null && !TOOLS_CHILD_KEYS.includes(f.key),
  );
  // A pillar with subpages but no feature row of its own gets a synthetic
  // header: a label to collapse under, no toggle data because there is
  // nothing in genesis to toggle. Tools is always synthetic (see above).
  const syntheticPillars = PILLAR_KEYS.filter(
    (key) => !featuresByKey.has(key) && (childrenByParent.get(key)?.length ?? 0) > 0,
  );
  const toolsGroupPresent = (childrenByParent.get(TOOLS_KEY)?.length ?? 0) > 0;

  type TopLevelEntry = { key: string; feature: Feature | null };
  const topLevelEntries: TopLevelEntry[] = [
    ...topLevelFeatures.map((feature) => ({ key: feature.key, feature })),
    ...syntheticPillars.map((key) => ({ key, feature: null })),
    ...(toolsGroupPresent ? [{ key: TOOLS_KEY, feature: null }] : []),
  ].sort((a, b) => {
    // The four pillars sort by PILLAR_ORDER (menu order); everything else
    // that isn't a pillar (search/assistant/chat - topbar icons, not sidebar
    // pillars) follows, alphabetically among themselves.
    const aPillarRank = PILLAR_ORDER.indexOf(a.key);
    const bPillarRank = PILLAR_ORDER.indexOf(b.key);
    if (aPillarRank !== -1 && bPillarRank !== -1) return aPillarRank - bPillarRank;
    if (aPillarRank !== -1) return -1;
    if (bPillarRank !== -1) return 1;
    return a.key.localeCompare(b.key);
  });

  function cellKey(featureKey: string, audienceId: string): string {
    return `${featureKey}::${audienceId}`;
  }

  function effectiveGrant(featureKey: string, audienceId: string, serverGranted: boolean): boolean {
    return pending[cellKey(featureKey, audienceId)] ?? serverGranted;
  }

  function effectiveGrantOf(feature: Feature, audienceId: string): boolean {
    return effectiveGrant(feature.key, audienceId, feature.audienceIds.includes(audienceId));
  }

  function setDesired(map: Record<string, boolean>, featureKey: string, audienceId: string, desired: boolean, serverGranted: boolean) {
    const key = cellKey(featureKey, audienceId);
    if (desired === serverGranted) delete map[key];
    else map[key] = desired;
  }

  // Turning a parent off for an audience implies its subpages too, and theirs:
  // reaching grow.map already requires grow itself (router.tsx nests the two
  // RequireFeature gates), and a control inside save.profile is unreachable
  // once the page is, so a still-on child toggle next to an off parent would
  // read as active when it can never actually be reached. Only cascades
  // downward, off - turning grow back on never re-grants a child the admin
  // explicitly turned off, additive-only stays one-directional.
  function toggleDraft(feature: Feature, audienceId: string) {
    const serverGranted = feature.audienceIds.includes(audienceId);
    const desired = !effectiveGrantOf(feature, audienceId);
    setPending((prev) => {
      const next = { ...prev };
      setDesired(next, feature.key, audienceId, desired, serverGranted);
      if (!desired) {
        const cascade = (parentKey: string) => {
          for (const child of childrenByParent.get(parentKey) ?? []) {
            setDesired(next, child.key, audienceId, false, child.audienceIds.includes(audienceId));
            cascade(child.key);
          }
        };
        cascade(feature.key);
      }
      return next;
    });
  }

  function toggleCollapsed(key: string, collapsed: boolean) {
    setCollapsedParents((prev) => ({ ...prev, [key]: !collapsed }));
  }

  function renderCollapseChevron(key: string, collapsed: boolean) {
    return (
      <button
        type="button"
        // The row itself carries the same toggle (see renderRow/
        // renderSyntheticHeaderRow) - stop the click here so it doesn't also
        // fire the row's handler and double up on the same state update.
        onClick={(e) => {
          e.stopPropagation();
          toggleCollapsed(key, collapsed);
        }}
        aria-expanded={!collapsed}
        className="rounded p-0.5 text-ink/40 transition hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
        aria-label={intl.formatMessage(
          collapsed
            ? { id: "admin.accessConfig.expandGroupLabel", defaultMessage: "Expand {feature}" }
            : { id: "admin.accessConfig.collapseGroupLabel", defaultMessage: "Collapse {feature}" },
          { feature: key },
        )}
      >
        <svg
          viewBox="0 0 20 20"
          fill="none"
          className={`h-3 w-3 transition-transform ${collapsed ? "-rotate-90" : ""}`}
        >
          <path d="M6 8l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    );
  }

  // A pillar header with subpages but no feature row behind it (save today):
  // same chevron and label as a real row, but no Switch cells - there is
  // nothing in genesis to toggle for "save" itself.
  function renderSyntheticHeaderRow(
    key: string,
    opts: { hasChildren: boolean; collapsed: boolean; showBorder: boolean },
  ) {
    // Every synthetic header names a real genesis feature key except Tools,
    // which spark invented purely to group these three - show a plain label
    // for it rather than the internal grouping id.
    const label = key === TOOLS_KEY ? "Tools" : key;
    return (
      <tr
        key={key}
        className={`last:border-0 transition-[background-color] ${opts.showBorder ? "border-b border-ink/5" : ""} ${opts.hasChildren ? "cursor-pointer hover:bg-ink/[0.06]" : ""}`}
        onClick={opts.hasChildren ? () => toggleCollapsed(key, opts.collapsed) : undefined}
      >
        <th scope="row" className="field-value px-4 py-2.5 font-normal text-ink/70">
          <span className="inline-flex items-center gap-1.5">
            {/* Fixed-width slot regardless of whether a chevron renders here -
                otherwise a childless pillar's label (SHARE, MATCH) starts
                further left than one with a chevron (SAVE, GROW, TOOLS). */}
            <span className="inline-flex w-4 shrink-0 justify-center">
              {opts.hasChildren ? renderCollapseChevron(key, opts.collapsed) : null}
            </span>
            <span className="uppercase tracking-[0.18em]">{label}</span>
          </span>
        </th>
        {audiences.map((audience) => (
          <td key={audience.id} className="px-4 py-2.5 text-center" />
        ))}
      </tr>
    );
  }

  // Row labels: a nav entry takes the sidebar's own translated label, a
  // feature that isn't one (a control inside a page) carries its label in the
  // catalog. Everything else falls back to the key, capitalised - for a nested
  // row only its last segment, the parent row already said the prefix.
  function featureLabel(feature: Feature, depth: number): string {
    const nav = NAV_LABEL_BY_FEATURE_KEY.get(feature.key);
    if (nav) return intl.formatMessage({ id: nav.id, defaultMessage: nav.defaultMessage });
    const catalog = CATALOG_BY_KEY.get(feature.key)?.label;
    if (catalog) return intl.formatMessage(catalog);
    return capitalize(depth > 0 ? feature.key.slice(feature.key.lastIndexOf(".") + 1) : feature.key);
  }

  // Indent per nesting depth: pillar, its subpage, a control inside that
  // subpage. One chevron per pillar only - deeper rows unfold with it.
  const ROW_INDENT: Record<number, string> = { 0: "px-4", 1: "pl-9 pr-4", 2: "pl-14 pr-4" };

  function renderRow(
    feature: Feature,
    opts: {
      depth: number;
      hasChildren: boolean;
      collapsed: boolean;
      showBorder: boolean;
      parent?: Feature;
    },
  ) {
    // The four pillars read like the sidebar headings they configure (SAVE,
    // GROW, SHARE, MATCH); everything nested or standalone keeps its label's
    // own case.
    const isPillarHeading = opts.depth === 0 && PILLAR_KEYS.includes(feature.key);
    const rowLabel = featureLabel(feature, opts.depth);
    return (
      <tr
        key={feature.key}
        className={`last:border-0 transition-[background-color] ${opts.showBorder ? "border-b border-ink/5" : ""} ${opts.hasChildren ? "cursor-pointer hover:bg-ink/[0.06]" : ""}`}
        onClick={opts.hasChildren ? () => toggleCollapsed(feature.key, opts.collapsed) : undefined}
      >
        <th
          scope="row"
          className={`field-value py-2.5 font-normal text-ink/70 ${ROW_INDENT[opts.depth] ?? ROW_INDENT[2]}`}
        >
          <span className="inline-flex items-center gap-1.5">
            {opts.depth === 0 ? (
              // Fixed-width slot regardless of whether a chevron renders
              // here - otherwise a childless pillar's label (SHARE, MATCH)
              // starts further left than one with a chevron (SAVE, GROW).
              <span className="inline-flex w-4 shrink-0 justify-center">
                {opts.hasChildren ? renderCollapseChevron(feature.key, opts.collapsed) : null}
              </span>
            ) : null}
            <span className={isPillarHeading ? "uppercase tracking-[0.18em]" : undefined}>
              {rowLabel}
            </span>
            {CATALOG_BY_KEY.get(feature.key)?.enforcement === "ui-only" ? (
              <span
                className="rounded-full bg-ink/[0.06] px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-ink/45"
                title={uiOnlyBadgeHint(intl)}
              >
                {intl.formatMessage({
                  id: "admin.accessConfig.uiOnlyBadgeLabel",
                  defaultMessage: "spark-side",
                })}
              </span>
            ) : null}
          </span>
        </th>
        {audiences.map((audience) => {
          const serverGranted = feature.audienceIds.includes(audience.id);
          const granted = effectiveGrant(feature.key, audience.id, serverGranted);
          const belowFloor = !granted && isBelowFloor(audience.id, feature.minAudienceId);
          const parentOff = !granted && !belowFloor && opts.parent ? !effectiveGrantOf(opts.parent, audience.id) : false;
          const label = intl.formatMessage(
            {
              id: "admin.accessConfig.toggleLabel",
              defaultMessage: "{feature} for {audience}",
            },
            { feature: feature.key, audience: audience.name?.localized ?? audience.id },
          );
          return (
            <td key={audience.id} className="px-4 py-2.5 text-center">
              {belowFloor ? (
                // Below the floor the switch itself stays visible,
                // a bare lock glyph read as "broken toggle" on its
                // own, but a lock badge on top makes clear this
                // one can never be flipped, unlike a merely-off one.
                // stopPropagation: the row itself toggles collapse when it
                // has children (see the <tr> above) - a click meant for the
                // switch must not also fold the group it sits in. Scoped to
                // this wrapper, not the whole <td>, so the empty padding
                // around the switch still folds the row like the rest of it.
                <span
                  className="relative inline-flex"
                  title={belowFloorHint(intl)}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Switch
                    checked={false}
                    onChange={() => {}}
                    disabled
                    ariaLabel={intl.formatMessage(
                      {
                        id: "admin.accessConfig.belowFloorLabel",
                        defaultMessage: "{label} (below the feature's minimum audience)",
                      },
                      { label },
                    )}
                  />
                  <span
                    aria-hidden="true"
                    className="absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-page text-ink/45 ring-1 ring-ink/10"
                  >
                    <svg viewBox="0 0 20 20" fill="none" className="h-2.5 w-2.5">
                      <rect x="4.5" y="8.5" width="11" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
                      <path
                        d="M7 8.5V6a3 3 0 0 1 6 0v2.5"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                </span>
              ) : (
                <span className="inline-flex" onClick={(e) => e.stopPropagation()}>
                  <Switch
                    checked={granted}
                    onChange={() => toggleDraft(feature, audience.id)}
                    disabled={busy || parentOff}
                    ariaLabel={
                      parentOff
                        ? intl.formatMessage(
                            {
                              id: "admin.accessConfig.requiresParentLabel",
                              defaultMessage: "{label} (requires {parent} enabled for this audience first)",
                            },
                            { label, parent: opts.parent?.key ?? "" },
                          )
                        : label
                    }
                    title={parentOff ? requiresParentHint(intl, opts.parent?.key ?? "") : undefined}
                  />
                </span>
              )}
            </td>
          );
        })}
      </tr>
    );
  }

  return (
    <SettingsCard
      title={intl.formatMessage({
        id: "admin.accessConfig.featuresTitle",
        defaultMessage: "Feature whitelist",
      })}
      sub={intl.formatMessage({
        id: "admin.accessConfig.featuresIntro",
        defaultMessage:
          "Which audiences reach which feature. Additive only: an empty row hides the feature from everyone.",
      })}
    >
      {loading && !data ? (
        <div aria-busy="true">
          <div aria-hidden="true" className="smycles-skeleton h-6 w-2/3 rounded" />
          <div aria-hidden="true" className="smycles-skeleton mt-3 h-6 w-1/2 rounded" />
          <div aria-hidden="true" className="smycles-skeleton mt-3 h-6 w-3/5 rounded" />
        </div>
      ) : error ? (
        <p className="text-sm text-accent" role="alert">
          {userErrorMessage(intl, error)}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full table-fixed text-left">
            <thead>
              <tr className="border-b border-ink/10">
                <th scope="col" className="field-label w-72 px-4 py-2.5 font-normal">
                  {intl.formatMessage({ id: "admin.accessConfig.feature", defaultMessage: "Feature" })}
                </th>
                {audiences.map((audience) => {
                  const name = audience.name?.localized ?? audience.id;
                  const description = audienceDescriptions[audience.id];
                  return (
                    <th key={audience.id} scope="col" className="field-label px-4 py-2.5 text-center font-normal">
                      <span className="inline-flex items-center gap-1">
                        {name}
                        {description ? (
                          <InfoTooltip
                            ariaLabel={intl.formatMessage(
                              {
                                id: "admin.accessConfig.audienceInfoLabel",
                                defaultMessage: "Who is {audience}?",
                              },
                              { audience: name },
                            )}
                            title={name}
                          >
                            {description}
                          </InfoTooltip>
                        ) : null}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {topLevelEntries.flatMap((entry) => {
                const children = childrenByParent.get(entry.key) ?? [];
                const collapsed = collapsedParents[entry.key] ?? true;
                // Separator lines mark where one "big page" (pillar/Tools)
                // ends and the next begins - never between a header and its
                // own first subpage, and never between two subpages. So the
                // header only draws one when it has nothing expanded below
                // it, and among expanded children only the last one draws
                // one, right before the next big page starts.
                const expanded = !collapsed && children.length > 0;
                // Descendants flattened depth-first: a subpage's own children
                // (a control inside it) follow it directly, one indent deeper,
                // and unfold together with the pillar.
                const descendants: Array<{ feature: Feature; depth: number; parent?: Feature }> = [];
                const collect = (parentKey: string, parent: Feature | undefined, depth: number) => {
                  for (const child of childrenByParent.get(parentKey) ?? []) {
                    descendants.push({ feature: child, depth, parent });
                    collect(child.key, child, depth + 1);
                  }
                };
                if (expanded) collect(entry.key, entry.feature ?? undefined, 1);
                const rows = [
                  entry.feature
                    ? renderRow(entry.feature, {
                        depth: 0,
                        hasChildren: children.length > 0,
                        collapsed,
                        showBorder: !expanded,
                      })
                    : renderSyntheticHeaderRow(entry.key, {
                        hasChildren: children.length > 0,
                        collapsed,
                        showBorder: !expanded,
                      }),
                ];
                descendants.forEach((d, i) => {
                  rows.push(
                    renderRow(d.feature, {
                      depth: d.depth,
                      hasChildren: false,
                      collapsed: false,
                      showBorder: i === descendants.length - 1,
                      parent: d.parent,
                    }),
                  );
                });
                return rows;
              })}
            </tbody>
          </table>
        </div>
      )}
    </SettingsCard>
  );
}

function belowFloorHint(intl: ReturnType<typeof useIntl>): string {
  return intl.formatMessage({
    id: "admin.accessConfig.belowFloorHint",
    defaultMessage: "Below this feature's minimum audience, refused by the server.",
  });
}

function uiOnlyBadgeHint(intl: ReturnType<typeof useIntl>): string {
  return intl.formatMessage({
    id: "admin.accessConfig.uiOnlyBadgeHint",
    defaultMessage:
      "No server-side guard exists for this feature. Toggling it here only shows or hides the navigation entry in spark; genesis does not refuse the underlying data. An interim measure until this feature gets its own server-side enforcement.",
  });
}

function requiresParentHint(intl: ReturnType<typeof useIntl>, parentKey: string): string {
  return intl.formatMessage(
    {
      id: "admin.accessConfig.requiresParentHint",
      defaultMessage: "Requires {parent} enabled for this audience first.",
    },
    { parent: parentKey },
  );
}
